/// <reference path="./lancer-data.d.ts" />
/**
 * Brand + lore reads off the core data that aren't indexed Compendium item types.
 *
 * Manufacturers and pilot backgrounds ship in `@massif/lancer-data` but are not
 * among the Compendium's queryable item types (frames/weapons/systems/…). The
 * frontend can't import `@massif/lancer-data` directly (it isn't a frontend
 * dependency — only this package depends on it), so these thin accessors expose
 * that bundled data through the package that owns it. Shapes are validated
 * loosely; tighten when the pilot/mech math reads specific fields.
 */
import lancerData from "@massif/lancer-data";

/** A mech manufacturer's brand record (manufacturers.json). */
export type Manufacturer = {
  id: string;
  name: string;
  /** Logo asset key (e.g. "gms", "ips-n"). */
  logo: string;
  /** Brand color on a light field. */
  light: string;
  /** Brand color on a dark field. */
  dark: string;
  /** In-fiction marketing tagline (may contain HTML). */
  quote?: string;
  description?: string;
}

/** A pilot background (backgrounds.json). */
export type Background = {
  id: string;
  name: string;
  description?: string;
  /** Suggested skill-trigger ids for this origin (RAW: a suggestion, not a grant). */
  skills?: string[];
}

/** A pilot skill trigger (skills.json). */
export type Skill = {
  id: string;
  name: string;
  description?: string;
}

const data = lancerData as {
  manufacturers?: Manufacturer[];
  backgrounds?: Background[];
  skills?: Skill[];
};

/** Every core manufacturer, in load order. */
export function listManufacturers(): Manufacturer[] {
  return (data.manufacturers ?? []).slice();
}

/** Every core pilot background, in load order. */
export function listBackgrounds(): Background[] {
  return (data.backgrounds ?? []).slice();
}

/** Every core pilot skill trigger, in load order. */
export function listSkills(): Skill[] {
  return (data.skills ?? []).slice();
}
